// @ts-nocheck
import { Stack, Typography } from "@mui/material";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useDataGrid } from "hooks/useDataGrid";
import { useTabs } from "hooks/useTabs";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { hideSpinner, showSpinner } from "store/reducers/spinnerReducer";

export const useReferralDetails = () => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning, userData } = useContext(GlobalContext);
  const role = userData?.role;
  const isSuperAdmin = !["Admin", "Marketer", "Scheduler"].includes(role);

  const { id } = useParams();
  const [data, setData] = useState({});
  const [admins, setAdmins] = useState([]);
  const [marketers, setMarketers] = useState([]);
  const [schedulers, setSchedulers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [hardButton, setHardButton] = useState(null);
  const [patient, setPatient] = useState(null);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingSuggestionsDeclined, setIsProcessingSuggestionsDeclined] = useState(false);
  const isDataLoaded = useRef(false);
  const [locations, setLocations] = useState([]);

  const { currentTab, handleChangeTab, reloadSignal } = useTabs({
    key: "ReferralDetails",
  });

  const dataGrid = useDataGrid({
    url: `/referral_history`,
    defaultSorter: {
      name: "change_date",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-referral-history-logs`,
    reloadIntervalInSeconds: 60,
    additionalParams: useMemo(
      () => ({ id, cognitoId: userData.cognitoId }),
      [id, userData.cognitoId]
    ),
  });

  const getData = createApiCall(
    async () => {
      const result = await API.get("restApi", `/referrals/${id}`, {});
      setData(result?.Item ?? {});
      setAdmins(result?.admins ?? []);
      setMarketers(result?.marketers ?? []);
      setSchedulers(result?.schedulers ?? []);
      setProviders(result?.providers ?? []);
      setHardButton(result?.hardButton ?? null);
      setPatient(result?.patient ?? null);
      setFiles(result?.files ?? []);
      setLocations(result?.locations ?? []);
      setIsLoading(false);
      isDataLoaded.current = true;
    },
    () => setIsLoading(false)
  );

  const setStatusToProcessing = (data) =>
    createApiCall(async () => {
      const { id, note, status } = data;
      const result = await API.put("restApi", "/referrals", {
        body: {
          id,
          status,
          note,
        },
      });

      if (result?.error) {
        console.error(result.error);
        dispatch(showError(result.error));
        return;
      }

      setData((prev) => ({ ...prev, status }));
    });

  const startProcessing = () => {
    setStatusToProcessing({
      id: data.id,
      note: data.note,
      status: "Processing",
    })();
    setIsProcessingSuggestionsDeclined(true);
  }

  const declineProcessingSuggestions = () => {
    setIsProcessingSuggestionsDeclined(true);
  };

  const handleResendFormLink = createApiCall(async () => {
    dispatch(showSpinner());
    try {
      const result = await API.post("restApi", `/resend_form_link`, {
        body: {
          referralId: id,
          type,
        },
      });

      if (result?.error) {
        dispatch(showError(result.error));
        return;
      }

      showWarning({
        title: (
          <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Success
            </Typography>
          </Stack>
        ),
        content: <>The form link has been successfully resent.</>,
      });
    } catch (error) {
      dispatch(showError(error?.message ?? "An error occurred"));
    } finally {
      dispatch(hideSpinner());
    }
  });

  const {
    createdAt,
    status,
    note,
    buttonId,
    buttonType,
    practiceName,
    practiceSpeciality,
    practiceAddress,
    practicePhone,
    practiceMarketingRegion,
    originatorName,
    originatorPhone,
    originatorEmail,
    originatorJobTitle,
    recipientName,
    recipientPhone,
    recipientAddress,
    type,
    question,
  } = data;

  let options = isSuperAdmin
    ? ["New", "Processing", "Completed", "Canceled"]
    : ["New", "Processing", "Completed"];

  if (type === "Notification") {
    options = isSuperAdmin
      ? ["New", "Processing", "Completed", "Canceled"]
      : ["New", "Processing", "Completed"];
  }

  const isResendButtonVisible =
    (["Notification", "Question"].includes(type) ||
      (type === "Referral" && !patient)) &&
    status !== "Completed" &&
    status !== "Canceled";

  const isProcessingSuggestionsVisible = status === "New" && !isProcessingSuggestionsDeclined && !isSuperAdmin;

  const handleStatusChange = (status) => {
    setStatusToProcessing({ id, note, status })();
  };

  const title = `${type ?? "Referral"} Details`;

  const isAttachmentTabVisible = files?.length > 0 && type !== "Notification";

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    dataGrid.loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reloadSignal]);

  useEffect(() => {
    handleChangeTab(null, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    id,
    title,
    data,
    admins,
    marketers,
    schedulers,
    providers,
    hardButton,
    patient,
    isLoading,
    currentTab,
    handleChangeTab,
    dataGrid,
    getData,
    createdAt,
    status,
    note,
    buttonId,
    buttonType,
    practiceName,
    practiceSpeciality,
    practiceAddress,
    practicePhone,
    practiceMarketingRegion,
    originatorName,
    originatorPhone,
    originatorEmail,
    originatorJobTitle,
    recipientName,
    recipientPhone,
    recipientAddress,
    type,
    question,
    options,
    isResendButtonVisible,
    handleStatusChange,
    handleResendFormLink,
    isSuperAdmin,
    files,
    isAttachmentTabVisible,
    isProcessingSuggestionsVisible,
    startProcessing,
    declineProcessingSuggestions,
    locations,
  };
};
